// src/hooks/useVoiceAssistant.js

import { useState, useEffect, useCallback, useRef } from 'react';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { Timestamp } from 'firebase/firestore';
import useCustomerOptions from './useCustomerOptions';
import { startOfDay, endOfDay } from 'date-fns';
import { numberToTurkishWords } from '../ai/helpers';
import { processCommand } from '../ai/commandProcessor';
import { speechService } from '../ai/speechService'; // Yeni servisi import ediyoruz

const useVoiceAssistant = () => {
    const [status, setStatus] = useState('idle');
    const [statusMessage, setStatusMessage] = useState('Asistanı başlatmak için butona tıklayın.');
    const audioRef = useRef(null);
    const [customers, setCustomers] = useState([]);
    const [conversationState, setConversationState] = useState(null);
    
    const speak = useCallback(async (textToSpeak, textToDisplay) => {
        setStatus('speaking');
        setStatusMessage(textToDisplay || textToSpeak);
        try {
            const synthesizeSpeech = httpsCallable(functions, 'synthesizeSpeech');
            const result = await synthesizeSpeech({ text: textToSpeak });
            const audioData = result.data.audioContent;
            const audioSrc = `data:audio/mp3;base64,${audioData}`;
            
            if (!audioRef.current) audioRef.current = new Audio();
            audioRef.current.src = audioSrc;
            await audioRef.current.play();

            audioRef.current.onended = () => {
                setStatus('idle');
                if (conversationState) {
                    startListening();
                }
            };
            audioRef.current.onerror = () => setStatus('idle');
        } catch (error) {
            console.error("Cloud function'ı çağırırken hata:", error);
            setStatusMessage("Ses üretilirken bir hata oluştu.");
            setStatus('idle');
        }
    }, [conversationState]); // startListening bağımlılığı kaldırıldı.

    const handleCommandProcessing = useCallback(async (command) => {
        // ... (Bu fonksiyonun içi aynı kalıyor)
        if (!command) {
            setStatus('idle');
            return;
        };

        setStatus('processing');
        setStatusMessage(`Komut anlaşıldı: "${command}". İşleniyor...`);

        if (command.toLowerCase() === 'iptal et' && conversationState) {
            setConversationState(null);
            await speak("İşlem iptal edildi.");
            return;
        }

        try {
            const responseText = await processCommand(command.toLowerCase(), customers, conversationState, setConversationState);
            if (responseText) {
                await speak(responseText);
            } else {
                 setStatus('idle');
            }
        } catch (error) {
            console.error("Asistan komut işlerken hata:", error);
            await speak("Üzgünüm, isteğinizi işlerken bir sorunla karşılaştım.", "Hata: İşlem Başarısız.");
            setConversationState(null);
        }
    }, [customers, conversationState, speak]);

    const startListening = useCallback(() => {
        if (status !== 'idle') return;

        setStatus('listening');
        setStatusMessage(conversationState ? 'Cevabınızı bekliyorum...' : 'Sizi dinliyorum...');
        speechService.start();
    }, [status, conversationState]);

    const stopListening = useCallback(() => {
        speechService.stop();
        setConversationState(null);
        setStatus('idle');
        setStatusMessage('Asistan durduruldu.');
    }, []);

    useEffect(() => {
        speechService.init({
            onResult: handleCommandProcessing,
            onError: (event) => {
                console.error("Speech Service Error:", event.error);
                setStatusMessage(event.error === 'not-allowed' ? "Mikrofon izni verilmedi." : 'Sizi duyamadım.');
                setStatus('idle');
            },
            onEnd: () => {
                setStatus(currentStatus => currentStatus === 'listening' ? 'idle' : currentStatus);
            }
        });
    }, [handleCommandProcessing]);
    
    // Müşteri listesi paylaşımlı oturum cache'inden gelir. Bu hook MainLayout içinde,
    // yani tüm uygulamayı sarıyor: eskiden her oturumda sesli asistan hiç
    // kullanılmasa bile 1324 müşteri dokümanı okunuyordu.
    const { customers: cachedCustomers } = useCustomerOptions();

    useEffect(() => {
        setCustomers(cachedCustomers);
    }, [cachedCustomers]);
    
    useEffect(() => {
        const playWelcomeMessage = async () => { /* ... */ };
        // Bu kısım değişmedi, bu yüzden kısa tutuyorum.
        // ...
    }, [speak]);

    const isListening = status === 'listening';
    const isSpeaking = status === 'speaking' || status === 'processing';

    return { isListening, isSpeaking, statusMessage, startListening, stopListening };
};

export default useVoiceAssistant;